import { Buffer } from 'buffer';

import { NgClass, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar, MatSnackBarLabel, MatSnackBarActions, MatSnackBarAction } from '@angular/material/snack-bar';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import {
  CommonIP,
  CommonIPLite,
  CommonIPState,
  CommonIPStateTrifoldFiltersDialogData,
  DialogMessageEvent,
  EA,
  ExcelActionDetails,
  IP,
  IpTypeLabel,
  Outlook,
  RelatedFileGroup,
  UploadStats,
  WorkflowDO,
  WsFile,
} from '@model/ftf-api.model';
import {
  convertCaseRefToReferenceDocument,
  convertCommonIPToIPCase,
  convertWsFileToDocument,
  DocumentAction,
  DocumentSelections,
  ReferenceDocument,
} from '@model/widget.model';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AccountService } from '@service/account.service';
import { AddinContextService, FTFEvent, SelectedItems } from '@service/addin-context.service';
import { ApiService, emptyCriteria } from '@service/api.service';
import { AuthService } from '@service/auth.service';
import { FavoriteService } from '@service/favorites.service';
import { HistoryService } from '@service/history.service';
import { LoaderService } from '@service/loader.service';
import { LoggerService } from '@service/logger.service';
import { PdfPreviewService } from '@service/pdf-preview.service';
import { PendoService } from '@service/pendo.service';
import { SettingsService } from '@service/settings.service';
import { VersionService } from '@service/version.service';
import { ExcelDownloadPrepareComponent } from '@shared/components/excel-download-prepare/excel-download-prepare.component';
import { IpDetailComponent } from '@shared/components/ip-detail/ip-detail.component';
import {
  areFiltersEqual,
  IpTrifoldComponent,
  TrifoldItemEvent,
  TrifoldItemFileEvent,
  TrifoldItemFileVersionEvent,
  TrifoldItemFilterEvent,
  TrifoldItemSelectionEvent,
  TrifoldItemTabEvent,
  TrifoldItemUploadEvent,
  TrifoldItemWorkflowEvent,
} from '@shared/components/ip-trifold/ip-trifold.component';
import { PaginatedAutocompleteComponent } from '@shared/components/paginated-autocomplete/paginated-autocomplete.component';
import * as ItemsActions from '@store/actions/items.actions';
import { selectAllItems, selectItemByIdentifierAndType } from '@store/selectors/items.selectors';
import { selectItemBySerialAndType } from '@store/selectors/items.selectors';
import { Cache } from '@utils/cache';
import { FileWithProgressHelper, convertFileToBase64 } from '@utils/file-utils';
import {
  FileBasedMailItem,
  getClassicWordContext,
  getDocumentAsCompressedObservable,
  getWordContext,
  MailItem,
  OutlookMailItem,
  WordRequestContext,
} from '@utils/office-utils';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  finalize,
  from,
  fromEvent,
  map,
  merge,
  mergeAll,
  mergeMap,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
  toArray,
} from 'rxjs';

import { UploadVersionDialogComponent, UploadVersionDialogData } from './upload-version-dialog/upload-version-confirm-dialog.component';
import { IpIconComponent } from '../../shared/components/ip-icon/ip-icon.component';
import { IpTrifoldComponent as IpTrifoldComponent_1 } from '../../shared/components/ip-trifold/ip-trifold.component';
import { PaginatedAutocompleteComponent as PaginatedAutocompleteComponent_1 } from '../../shared/components/paginated-autocomplete/paginated-autocomplete.component';
import { UploadResultsDialogComponent } from '../uploader/response-dialog/upload-results-dialog.component';

type ValidDragStateVars = Extract<keyof HomeComponent, 'isDragOver' | 'isDropPanelOver'>;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [
    ExcelDownloadPrepareComponent,
    MatToolbar,
    MatDivider,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    MatBadge,
    MatTooltip,
    NgClass,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    IpIconComponent,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatAutocompleteTrigger,
    MatPrefix,
    MatSelect,
    MatOption,
    PaginatedAutocompleteComponent_1,
    MatAccordion,
    IpTrifoldComponent_1,
    MatSnackBarLabel,
    MatSnackBarActions,
    MatButton,
    MatSnackBarAction,
    AsyncPipe,
    IpDetailComponent,
  ],
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  private accountService = inject(AccountService);
  private addinService = inject(AddinContextService);
  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private favoriteService = inject(FavoriteService);
  private formBuilder = inject(FormBuilder);
  private historyService = inject(HistoryService);
  private ngZone = inject(NgZone);
  private pdfPreviewService = inject(PdfPreviewService);
  private responseDialog = inject(MatDialog);
  private router = inject(Router);
  private settingsService = inject(SettingsService);
  private snackBar = inject(MatSnackBar);
  private store = inject(Store);
  private versionService = inject(VersionService);
  private loaderService = inject(LoaderService);
  private actions$ = inject(Actions);
  private loggerService = inject(LoggerService);
  private pendoService = inject(PendoService);

  private readonly $ngDestroyed = new Subject<void>();
  private readonly $ngReinit = new Subject<void>();
  private readonly $ngUploadDialogDestroyed = new Subject<void>();
  private currentPageIndex = new BehaviorSubject<number>(0);
  paginationInfo = new BehaviorSubject<{ currentPage: number; totalPages: number; totalRows?: number; resultLength?: number }>({
    currentPage: 1,
    totalPages: 1,
    totalRows: 1,
    resultLength: 1,
  });
  private cachedFiles: File[] = [];
  private nextUploaderTrifold: IP.TrifoldEnum = IP.TrifoldEnum.L;
  private subjectMissCache: Cache = new Cache(1000, 60000);
  private baseUrl = this.settingsService.getParsedBaseUrl();
  @ViewChild('autocomplete') autocomplete!: PaginatedAutocompleteComponent;
  @ViewChild('multiSelectWarning') warningTemplate!: TemplateRef<unknown>;
  @ViewChildren(IpTrifoldComponent) trifoldComponents!: QueryList<IpTrifoldComponent>;
  @ViewChild('dropPanelContainer') dropPanelContainer!: ElementRef<HTMLDivElement>;
  showUploadButton: boolean = true;
  items$: Observable<CommonIP[]>;
  searchItems$: Observable<CommonIP[]>;
  paginationInfo$ = this.paginationInfo.asObservable();
  searchHistoryItems: CommonIP[] = [];
  favoriteItems: CommonIP[] = [];
  form: FormGroup;
  ipTypes: IpTypeLabel<IP.TrifTypeEnum>[];
  isDragOver = false;
  isDropPanelOver = false;
  version = '';
  categories: Office.CategoryDetails[] = [];
  selectedEmails: SelectedItems = { items: [], exceedsLimit: false, count: 0 };
  canUploadNewVersion: (doc: WsFile) => boolean;
  isAll: boolean;
  isOfficeDragOver = false;
  activeExcelMenuAction: ExcelActionDetails | null = null;

  constructor() {
    this.isAll = true;
    this.canUploadNewVersion = (doc: WsFile) => {
      return this.isWord && /\.docx$/i.test(doc.fileName ?? '');
    };
    this.authService.canCallApi$.pipe(takeUntil(this.$ngDestroyed)).subscribe((canCallApi) => {
      if (!canCallApi) {
        // user sent to login
        this.router.navigateByUrl('/taskpane/login');
      }
    });
    this.ipTypes = this.accountService.trifIpTypes;
    this.form = this.formBuilder.group({
      search: [{ value: null, disabled: true }],
      ipType: [{ value: null, disabled: true }],
    });
    this.searchItems$ = this.createSearchItemsObservable();
    this.items$ = this.store.select(selectAllItems).pipe(takeUntil(this.$ngDestroyed));
    if (this.addinService.isOutlook) {
      this.addinService.outlookItems$
        .pipe(takeUntil(this.$ngDestroyed))
        .subscribe((selectedItems) => this.ngZone.run(() => (this.selectedEmails = selectedItems)));
    }
  }

  ngAfterViewInit(): void {
    // if load items by id isn't supported, then show a warning once per browser session
    if (this.addinService.isOutlook && !this.addinService.supportsMultiSelect && !sessionStorage.getItem('multiSelWarningShown')) {
      timer(3000)
        .pipe(take(1))
        .subscribe(() => {
          this.snackBar
            .openFromTemplate(this.warningTemplate, {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              politeness: 'assertive',
            })
            .afterDismissed()
            .subscribe(() => {
              sessionStorage.setItem('multiSelWarningShown', 'true');
            });
        });
    }
  }

  ngOnInit() {
    // load users preferences and permissions, etc..
    this.accountService.isAccountDetailsLoaded$.pipe(takeUntil(this.$ngDestroyed)).subscribe((loaded) => {
      if (!loaded) return;

      // signal to reinitialize the subscriptions we are about to resetup
      this.$ngReinit.next();
      this.listenAuthenticationStatus();

      // Enable only the IP Types that the user has access to
      this.ipTypes = this.accountService.trifIpTypes;

      this.showUploadButton = this.accountService.featureFlags.upload && this.settingsService.getSettings().navigationBarUploadOption;

      if (this.ipTypes.length > 0) {
        this.loadedSelectedIpType();
        this.cdr.detectChanges();
        this.form.enable();
      } else {
        this.form.disable();
      }
      if (this.addinService.isOutlook) {
        // initialize outlook categories if applicable
        this.initializeOutlookCategories();
        // subscribe to item change event
        this.addinService.outlookItems$
          .pipe(takeUntil(merge(this.$ngDestroyed, this.$ngReinit)), debounceTime(1000))
          .subscribe((selectedItems) => {
            const subjects = selectedItems.items.map((i) => i.subject);
            const settings = this.settingsService.getSettings();
            // Check for case numbers first
            if (settings.regExpActive) {
              this.loggerService.debug('[HomeComponent] Checking subject for case number', selectedItems);

              // autoSearchOnCaseFromSubject now returns an Observable<boolean>
              this.autoSearchOnCaseFromSubject(subjects).subscribe((caseNumberFound) => {
                // Only check for application numbers if no case number was found and appNumberMatching is enabled
                if (!caseNumberFound && settings.appNumberMatchingEnabled) {
                  this.loggerService.debug('[HomeComponent] No case number found, checking subject for application number', selectedItems);
                  this.autoSearchOnApplicationNumberFromSubject(subjects).subscribe();
                }
              });
            } else if (settings.appNumberMatchingEnabled) {
              // If regex matching is not enabled, proceed directly to application number check
              this.loggerService.debug('[HomeComponent] Checking subject for application number', selectedItems);
              this.autoSearchOnApplicationNumberFromSubject(subjects).subscribe();
            }
          });
        this.addinService.officeDragDrop$
          .pipe(takeUntil(this.$ngDestroyed))
          .subscribe((event) => this.ngZone.run(() => this.handleOfficeDragAndDrop(event)));
      }

      if (this.addinService.isWidgetActive) {
        this.initializeWidget();
      }
    });
    this.historyService.historyItemsSubject$.pipe(takeUntil(this.$ngDestroyed)).subscribe((items) => {
      this.searchHistoryItems = items;
    });

    this.favoriteService.favoriteItems$.pipe(takeUntil(this.$ngDestroyed)).subscribe((items) => {
      this.favoriteItems = items;
    });

    this.favoriteService.loadPreviousFavorite();
    this.version = this.versionService.getFullVersion();

    // handle download events being pushed from ngrx
    this.actions$.pipe(ofType(ItemsActions.downloadItemDoc), takeUntil(this.$ngDestroyed)).subscribe((action) => {
      const url = window.URL.createObjectURL(action.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = action.fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    });
  }

  listenAuthenticationStatus() {
    this.pendoService.initialize();
  }

  manageCookiePreferences() {
    this.pendoService.openCookiePreferences();
  }

  /**
   * Initializes the widget.
   */
  initializeWidget() {
    // if widget is active, we relay events for when a trifold is opened
    this.actions$.pipe(ofType(ItemsActions.addItemSuccess), takeUntil(merge(this.$ngDestroyed, this.$ngReinit))).subscribe((action) => {
      // record event for the opened item
      const item = convertCommonIPToIPCase(action.item);
      this.addinService.recordEvent(FTFEvent.CaseOpen, item);
    });

    // subscribe to addin service commands
    this.addinService.command$.pipe(takeUntil(merge(this.$ngDestroyed, this.$ngReinit))).subscribe(() =>
      this.ngZone.run(() => {
        const commands = this.addinService.dequeueCommands();
        commands.forEach((command) => {
          switch (command.type) {
            case 'openByAppSerNum': {
              // open case by serial no
              const ipTypes = this.checkAndSetIpType(command.args.length > 1 && !!command.args[1] ? command.args[1] : 'All');
              const filters = command.args.length > 2 && !!command.args[2] ? command.args[2] : {};
              if (ipTypes.length > 0) {
                this.openByReferenceAndTypes([command.args[0]], ipTypes, true, filters).subscribe(() => {
                  this.addinService.recordEvent(FTFEvent.Command, command);
                });
              }
              break;
            }
            case 'openByCaseRefNum': {
              // open case by case reference number
              const ipTypes = this.checkAndSetIpType(command.args.length > 1 && !!command.args[1] ? command.args[1] : 'All');
              const filters = command.args.length > 2 && !!command.args[2] ? command.args[2] : {};

              if (ipTypes.length > 0) {
                this.openByReferenceAndTypes([command.args[0]], ipTypes, false, filters).subscribe(() => {
                  this.addinService.recordEvent(FTFEvent.Command, command);
                });
              }
              break;
            }
          }
        });
      })
    );
  }

  /**
   * Initializes Outlook categories used by the add-in.
   */
  initializeOutlookCategories() {
    this.categories = [
      {
        displayName: Outlook.CategoryEnum.UPLOADED_CATEGORY,
        color: Office.MailboxEnums.CategoryColor.Preset4, // green
      },
      {
        displayName: Outlook.CategoryEnum.UPLOADED_CATEGORY_INCOMPLETE,
        color: Office.MailboxEnums.CategoryColor.Preset3, // yellow
      },
      {
        displayName: Outlook.CategoryEnum.UPLOADED_CATEGORY_FAILURE,
        color: Office.MailboxEnums.CategoryColor.Preset0, // red
      },
    ];

    // fetch all categories tied to the current outlook
    Office.context.mailbox.masterCategories.getAsync((asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        this.loggerService.error('[HomeComponent] Failed to retrieve categories', asyncResult.error);
        return;
      }

      // filter ftf categories array to only include those that don't already exist
      const existingCategories = asyncResult.value;
      const missingCategories = this.categories.filter((cat) => !existingCategories.some((ec) => ec.displayName === cat.displayName));
      if (missingCategories.length === 0) return;
      // add any missing ftf categories
      Office.context.mailbox.masterCategories.addAsync(missingCategories, (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          this.loggerService.error('[HomeComponent] Failed to add FTF categories', asyncResult.error);
        }
      });
    });
  }

  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
    this.$ngUploadDialogDestroyed.next();
    this.$ngUploadDialogDestroyed.complete();
    this.subjectMissCache.clear();
  }

  /**
   * Creates an observable for paginated search results based on form values and page index.
   * Updates pagination info and returns filtered results.
   *
   * @param displayNumber - Number of items to display per page
   * @returns Observable of CommonIP array containing search results
   */
  private createSearchItemsObservable(displayNumber: number = 10): Observable<CommonIP[]> {
    this.form.get('search')!.valueChanges.subscribe(() => {
      this.resetAutocomplete();
    });
    return combineLatest([
      this.form.get('search')!.valueChanges.pipe(startWith(this.form.get('search')!.value)),
      this.form.get('ipType')!.valueChanges.pipe(startWith(this.form.get('ipType')!.value)),
      this.currentPageIndex.asObservable(),
    ]).pipe(
      takeUntil(this.$ngDestroyed),
      debounceTime(1000),
      map(() => {
        const search = this.form.get('search')?.value;
        const ipType = this.form.get('ipType')?.value;
        const pageIndex = this.currentPageIndex.value;
        return [search, ipType, pageIndex];
      }),
      switchMap(([search, ipType, pageIndex]) => {
        if (!!search && !!ipType) {
          return this.apiService.findIPItemsContaining(
            search,
            ipType === 'All' ? this.ipTypes.map((i) => i.key) : [ipType],
            pageIndex * displayNumber,
            displayNumber
          );
        } else {
          return of(emptyCriteria<CommonIP>(pageIndex * displayNumber, displayNumber));
        }
      }),
      tap((criteria) => this.updatePaginationInfo(criteria, displayNumber)),
      map((criteria) => criteria.results)
    );
  }

  /**
   * Resets the autocomplete component and current page index.
   */
  private resetAutocomplete() {
    if (this.autocomplete) {
      this.autocomplete.reset();
    }
    this.currentPageIndex.next(0);
  }

  /**
   * Updates pagination information based on search criteria.
   * Triggers change detection after update.
   *
   * @param criteria - The search criteria containing result information
   * @param displayNumber - Number of items displayed per page
   */
  private updatePaginationInfo(criteria: { totalRows: number; currentPage?: number }, displayNumber: number): void {
    this.ngZone.run(() => {
      const totalPages = Math.max(1, Math.ceil(criteria.totalRows / displayNumber));
      const currentPage = Math.floor((criteria.currentPage ?? 0) / displayNumber) + 1;
      const resultLength = displayNumber;
      const { totalRows } = criteria;
      this.paginationInfo.next({
        currentPage,
        totalPages,
        totalRows,
        resultLength,
      });
      this.cdr.detectChanges();
    });
  }

  /**
   * Handles page change events.
   * @param pageIndex The new page index.
   */
  onPageChange(pageIndex: number): void {
    this.ngZone.run(() => {
      this.currentPageIndex.next(pageIndex);
      this.cdr.detectChanges(); // Force change detection
    });
  }

  /**
   * Handles drag over events.
   * @param event The drag event.
   * @param stateVars The state variables to update.
   * @returns
   */
  onDragOver(event: DragEvent, stateVars: ValidDragStateVars[] = ['isDragOver']) {
    event.preventDefault();
    if (!this.accountService.featureFlags.upload) return;
    stateVars.forEach((varName) => (this[varName] = true));
  }

  /**
   * Handles drag leave events.
   * @param event
   * @param container
   * @param stateVars
   */
  onDragLeave(event: DragEvent, container: HTMLDivElement, stateVars: ValidDragStateVars[] = ['isDragOver']) {
    event.preventDefault();
    if (!container.contains(event.relatedTarget as Node)) {
      stateVars.forEach((varName) => (this[varName] = false));
    }
  }

  /**
   * Handles drop events.
   * @param event The drag event.
   * @param stateVars The state variables to update.
   * @returns
   */
  onDrop(event: DragEvent, stateVars: ValidDragStateVars[] = ['isDragOver']) {
    event.preventDefault();

    if (event.dataTransfer?.files?.length) {
      let ipType = this.form.get('ipType')?.getRawValue();
      if (this.accountService.hasRole('ROLE_REFERENCE_CREATE')) {
        // for main drag area, default to references if they have access
        ipType = IP.TypeEnum.Reference;
      }
      if (!ipType) {
        this.loggerService.debug('[HomeComponent] No IP Type selected. Unable to launch upload.');
        return;
      }
      const trifold = this.determineDefaultTrifold();
      const files = Array.from(event.dataTransfer.files);
      this.openUploadDialog('', ipType, trifold, files, true);
    } else {
      this.loggerService.error('[HomeComponent] No files dropped.');
    }
    stateVars.forEach((varName) => (this[varName] = false));
  }

  /**
   * Handles trifold drag over events.
   * @param event The drag event.
   * @param item The item being dragged.
   * @param trifoldComp The trifold component.
   */
  onTrifoldDragOver(event: DragEvent, item: CommonIP, trifoldComp: IpTrifoldComponent) {
    event.preventDefault();
    this.updateItemState(item, { isDragOver: trifoldComp.canUploadAnyTrifolds });
  }

  /**
   * Handles trifold drag leave events.
   * @param event The drag event.
   * @param item The item being dragged.
   * @param container The container element.
   */
  onTrifoldDragLeave(event: DragEvent, item: CommonIP, container: HTMLDivElement) {
    event.preventDefault();
    if (!container.contains(event.relatedTarget as Node)) {
      this.updateItemState(item, { isDragOver: false });
    }
  }

  /**
   * Handles trifold drop events.
   * @param event The drag event.
   * @param item The item being dragged.
   * @param trifoldComp The trifold component.
   */
  onTrifoldDrop(event: DragEvent, item: CommonIP, trifoldComp: IpTrifoldComponent) {
    event.preventDefault();
    this.updateItemState(item, { isDragOver: false });
    this.isDragOver = false;

    if (event.dataTransfer?.files?.length && trifoldComp.canUploadAnyTrifolds) {
      const files = Array.from(event.dataTransfer.files) ?? [];
      const trifold = trifoldComp.uploadTrifold;
      this.openUploadDialog(item.identifier, item.ipType, trifold, files, true);
    }
  }

  /**
   * Handles the change event when a selection is made in the search autocomplete.
   * @param $event - The MatAutocompleteSelectedEvent object containing the selected option.
   */
  onSearchSelectionChange($event: MatAutocompleteSelectedEvent) {
    const item = $event.option.value as CommonIP;
    // open the item
    this.fetchAndAddItem(item);
    // get rid of selection / value
    this.form.get('search')?.setValue(null);
    $event.option.deselect();
  }

  /**
   * Handles the click event on a list item.
   * @param searchObj The search object representing the clicked item.
   */
  onListItemClick(searchObj: CommonIP) {
    this.fetchAndAddItem(searchObj);
  }

  /**
   * Handles the change event when the IP type selection is changed.
   */
  onIpTypeSelectionChange() {
    this.form.get('search')?.setValue(null);
    const ipType = this.form.get('ipType')?.getRawValue();
    this.isAll = ipType && ipType === 'All';
    this.resetAutocomplete();
  }

  /**
   * Loads the selected IP type from settings and updates the form accordingly.
   */
  loadedSelectedIpType() {
    const settings = this.settingsService.getSettings();
    const selectedIpType = settings.ipTypeDefault;
    if (selectedIpType) {
      this.form.setValue({
        search: '',
        ipType: selectedIpType,
      });
    } else {
      this.form.setValue({
        search: '',
        ipType: 'Patent',
      });
    }
  }

  /**
   * Handles the event when an item is closed.
   * @param event - The trifold event that triggered the close action.
   */
  onCloseItem(event: TrifoldItemEvent) {
    this.removeItem(event.item);
  }

  /**
   * Handles the logout action.
   */
  onLogout() {
    this.authService.logout(true);
  }

  /**
   * Handles the event when an item is favorited or unfavorited.
   * @param event - The trifold event that triggered the favoriting action.
   */
  onFavoritingCase(event: TrifoldItemEvent) {
    const { item } = event;
    let saved = true;
    if (item.favorite) {
      this.favoriteService.removeFavorite(item);
    } else {
      saved = this.favoriteService.saveFavorite(item);
    }
    if (saved) {
      // update favorite state
      this.updateItem({ identifier: item.identifier, ipType: item.ipType, favorite: !item.favorite });
    }
  }

  /**
   * Handles the event when the view is changed.
   * @param event - The trifold event that triggered the view change.
   */
  onViewChange(event: TrifoldItemTabEvent) {
    this.updateItemState(event.item, { view: event.view });
  }

  onTabChange(event: TrifoldItemTabEvent) {
    if (event.view === 'trifold') {
      this.updateItemState(event.item, { trifoldTabIndex: event.tabIndex });
    } else {
      this.updateItemState(event.item, { detailTabIndex: event.tabIndex });
    }
  }

  /**
   * Handles the event when the expansion state is toggled.
   * @param event - The trifold event that triggered the toggle action.
   */
  onToggleExpansion(event: TrifoldItemEvent) {
    this.updateItemState(event.item, { expanded: !event.item.state?.expanded });
  }

  /**
   *  Handles the event when filters are applied.
   * @param event - The trifold event that triggered the filter action.
   */
  onUpdateVersion(event: TrifoldItemFileVersionEvent) {
    this.updateItemTrifoldDocument(event.item, event.trifold, event.doc.fileId!, { versions: event.version });
  }

  /**
   * Opens the specified URL in IPMS.
   * @param event - The trifold event that triggered the method.
   */
  onOpenInIPMS(event: TrifoldItemEvent) {
    if (this.addinService.isOfficeActive) {
      window.open(event.item.caseUrl!, '_blank');
    } else {
      window.open(event.item.caseUrl!, '_blank');
    }
  }

  /**
   * Handles the event when files are uploaded.
   * @param files - The files to be uploaded.
   */
  onOpenUploadGeneric(files: File[] = []) {
    let ipType: IP.TypeEnum = this.form.get('ipType')?.getRawValue();
    if (this.accountService.hasRole('ROLE_REFERENCE_CREATE')) {
      ipType = IP.TypeEnum.Reference;
    }
    const trifold = this.determineDefaultTrifold();
    if (this.addinService.isOutlookCompose) {
      const item = this.addinService.outlookItem;
      if (item) {
        this.loaderService.show();
        (item as OutlookMailItem)
          .getComposeAsFile()
          .pipe(finalize(() => this.loaderService.hide()))
          .subscribe((file) => {
            this.openUploadDialog('', ipType, trifold, [file]);
          });
      }
    } else {
      this.openUploadDialog('', ipType, trifold, files);
    }
  }

  onOpenUploadTrifold(event: TrifoldItemUploadEvent): void {
    if (this.isWord) {
      getClassicWordContext(this.loggerService).subscribe((context) => {
        // check that document is saved
        if (!context.document.saved) {
          this.snackBar.open('Please save the document before uploading.', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            politeness: 'assertive',
            duration: 5000,
          });
          return;
        }

        this.openUploadDialog(event.item.identifier, event.item.ipType, event.trifold, event.files ?? [], true);
      });
    } else if (this.addinService.isOutlookCompose) {
      const item = this.addinService.outlookItem;
      if (item) {
        this.loaderService.show();
        (item as OutlookMailItem)
          .getComposeAsFile()
          .pipe(finalize(() => this.loaderService.hide()))
          .subscribe((file) => {
            this.openUploadDialog(event.item.identifier, event.item.ipType, event.trifold, [file], true);
          });
      }
    } else {
      this.openUploadDialog(event.item.identifier, event.item.ipType, event.trifold, event.files ?? [], true);
    }
  }

  /**
   * Opens the upload dialog.
   *
   * @param identifier - The identifier for the upload dialog.
   * @param ipType - The IP type for the upload dialog.
   * @param trifold - Trifold to default file to
   * @param files - The files to be uploaded.
   * @param isTrifoldUpload - Indicates if this is a trifold upload.
   */
  openUploadDialog(
    identifier = '',
    ipType: IP.TrifTypeEnum | IP.TypeEnum,
    trifold: IP.TrifoldEnum,
    files: File[] = [],
    isTrifoldUpload: boolean = false
  ) {
    this.cachedFiles = files;
    this.nextUploaderTrifold = trifold;
    this.loggerService.debug(`[HomeComponent] Opening upload dialog for ${identifier} (${ipType}) with ${files.length} files`);

    const absoluteUrl = new URL(
      `/taskpane/uploader?ipType=${encodeURIComponent(ipType)}&identifier=${encodeURIComponent(identifier)}&trifold=${encodeURIComponent(trifold)}&isTrifoldUpload=${isTrifoldUpload}`,
      new URL(window.location.href)
    );
    const url = absoluteUrl.toString();

    const options = {
      promptBeforeOpen: false,
      displayInIframe: true,
      height: 70,
      width: 90,
    };

    if (this.addinService.isOfficeActive) {
      // office's unique way of opening a dialog
      Office.context.ui.displayDialogAsync(url, options, (resp) => {
        if (resp.status !== Office.AsyncResultStatus.Succeeded) {
          this.loggerService.error('[HomeComponent] Error opening dialog:', resp.error);
        }
        const dialog = resp.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args) => {
          this.loggerService.debug('[HomeComponent] Dialog Message received:', args);
          if ('message' in args) {
            this.onUploadDialogMessage(args.message, dialog);
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
          this.loggerService.debug('[HomeComponent] Dialog Event received:', args);
          if ('error' in args && args.error === 12006) {
            // dialog was closed.. so cleanup
            this.cachedFiles = [];
            // Signal to cancel any in-progress observables
            this.$ngUploadDialogDestroyed.next();
          }
        });
      });
    } else {
      const dialog = window.open(url, '_blank');
      if (dialog) {
        fromEvent<MessageEvent>(dialog, 'message')
          .pipe(takeUntil(this.$ngUploadDialogDestroyed))
          .subscribe((event: MessageEvent) => {
            if (window !== event.source) {
              this.onUploadDialogMessage(event.data, dialog);
            }
          });
        // Periodically check if the dialog is closed
        const cleanupUploadDialog = () => {
          if (dialog.closed) {
            this.loggerService.debug('[HomeComponent] Upload Dialog closed');
            // Signal to cancel any in-progress observables
            this.$ngUploadDialogDestroyed.next();
            // cleanup cached infos
            this.cachedFiles = [];
            // remove cleanup timer
            clearInterval(timer);
          }
        };
        const timer = setInterval(cleanupUploadDialog.bind(this), 1000);
      }
    }
  }

  /**
   * Handles the upload dialog message.
   *
   * @param messageString - The message string received from the upload dialog.
   * @param dialog - The upload dialog object.
   */
  onUploadDialogMessage(messageString: string, dialog: Office.Dialog | Window) {
    if (!/^{.*?"type"\s*:.*}/s.test(messageString)) return;
    const message = JSON.parse(messageString) as DialogMessageEvent;
    this.loggerService.debug('[HomeComponent] Dialog Message received:', message);

    if (message.type === 'dialogLoaded') {
      this.messageUploadDialog({ type: 'dialogLoadedAck' }, dialog);
      // send the files to the dialog
      if (this.cachedFiles.length) {
        this.sendUploadDialogFiles(this.cachedFiles, this.nextUploaderTrifold, dialog);
        this.cachedFiles = [];
        // send the email item to the dialog
      } else if (this.addinService.isOutlookRead) {
        const items = this.addinService.outlookItems;
        const obs = items.map((item) => this.sendUploadDialogOutlookItem(item, this.nextUploaderTrifold, dialog));
        // if we have multiple items, then we need to limit the concurrency (office doesn't support it for multiselect emails)
        this.loaderService.show();
        from(obs)
          .pipe(
            takeUntil(this.$ngUploadDialogDestroyed),
            mergeMap((obs) => obs, items.length <= 1 ? 2 : 1),
            finalize(() => this.loaderService.hide())
          )
          .subscribe();
      } else if (this.addinService.isWord) {
        getWordContext(this.loggerService).subscribe((context) => this.sendUploadDialogWordItem(context, this.nextUploaderTrifold, dialog));
      }
    } else if (message.type === 'refreshTrifolds') {
      // refresh the trifold
      this.refreshItems(message.data.refreshItems as CommonIPLite[], message.data.stats);
      if (this.addinService.isOutlookRead && message.data.stats) {
        // update the categories based on stats provided back from upload results
        this.updateOutlookCategories(message.data.stats);
      }
      dialog.close();
    } else if (message.type === 'closeDialog') {
      dialog.close();
    }
  }

  onExcelMenuActionClick(action: EA.ActionEnum) {
    const settings = this.settingsService.getSettings();
    const actionType: EA.ActionTypeEnum =
      this.activeExcelMenuAction?.actionType || this.form.get('ipType')?.value || settings.ipTypeDefault;

    this.activeExcelMenuAction = {
      action,
      actionType,
      sourceType: 'Selection',
      source: [],
    };
  }

  onCloseExcelMenuAction() {
    this.activeExcelMenuAction = null;
  }

  /**
   * Sends files to the upload dialog.
   *
   * @param {WordRequestContext} context
   * @param {IP.TrifoldEnum} defaultTrifold
   * @param {(Window | Office.Dialog)} dialog
   */
  sendUploadDialogWordItem(context: WordRequestContext, defaultTrifold: IP.TrifoldEnum, dialog: Window | Office.Dialog): void {
    // convert the document
    const wordData = FileWithProgressHelper.fromWordItem(context, defaultTrifold);

    const data: RelatedFileGroup = { id: wordData.id, display: wordData.title, files: [wordData] };
    this.loaderService.show();
    this.messageUploadDialog({ type: 'fileGroup', data }, dialog);

    // load the content of the Docx
    getDocumentAsCompressedObservable()
      .pipe(
        mergeMap((blob) => convertFileToBase64(blob)),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (base64) => {
          this.messageUploadDialog({ type: 'fileData', data: { id: wordData.id, data: base64 } }, dialog);
        },
        error: () => {
          this.messageUploadDialog({ type: 'fileDataError', data: { id: wordData.id } }, dialog);
        },
      });
  }

  /**
   * Updates Outlook categories based on upload statistics.
   * @param stats
   */
  updateOutlookCategories(stats: UploadStats) {
    const items = this.addinService.outlookItems;
    from(items)
      .pipe(
        mergeMap(
          (item) => {
            const emailStat = stats.emails[item.itemId ?? ''];
            if (!emailStat) return of();

            const total = emailStat.success + emailStat.failure;
            if (total === 0) return of();

            // now add appropriate category based on success/failure
            let category = Outlook.CategoryEnum.UPLOADED_CATEGORY;
            if (emailStat.success > 0 && emailStat.failure > 0) {
              category = Outlook.CategoryEnum.UPLOADED_CATEGORY_INCOMPLETE;
            } else if (emailStat.failure > 0) {
              category = Outlook.CategoryEnum.UPLOADED_CATEGORY_FAILURE;
            }
            this.loggerService.debug(`[HomeComponent] Adding category ${category} to email ${item.subject}`);
            return item.replaceCategories(
              this.categories.map((cat) => cat.displayName),
              [category]
            );
            // limit the concurrency when multiple emails are selected (requirement for Outlook Items that are Loaded by ID)
          },
          items.length <= 1 ? 3 : 1
        ),
        catchError((err) => {
          this.loggerService.error('[HomeComponent] Error updating categories', err);
          return of();
        })
      )
      .subscribe();
  }

  /**
   * Initiates a search for cases based on the subject titles.
   * @param titles The subject titles to search within.
   * @returns An observable indicating the success or failure of the search.
   */
  autoSearchOnCaseFromSubject(titles: string[]): Observable<boolean> {
    const settings = this.settingsService.getSettings();
    const ipType: string = this.form.get('ipType')?.value ?? settings.ipTypeDefault;

    if (settings.regExpActive) {
      const regExp = new RegExp(settings.regExp!, 'i');
      // extract identifiers from dedup them
      const identifiers = titles
        .map((title) => {
          const match: RegExpMatchArray | null = regExp.exec(title);
          return match && match[1] ? match[1] : null;
        })
        .filter((title) => title !== null) as string[];

      if (identifiers.length > 0) {
        const ipTypeArray = this.checkAndSetIpType(ipType, false);
        return this.openByReferenceAndTypes(identifiers, ipTypeArray);
      }
    }
    return of(false); // No case number was found
  }

  /**
   * Initiates a search for application numbers based on the subject titles.
   * @param titles The subject titles to search within.
   * @returns An observable indicating the success or failure of the search.
   */
  autoSearchOnApplicationNumberFromSubject(titles: string[]): Observable<boolean> {
    const settings = this.settingsService.getSettings();

    if (settings.appNumberMatchingEnabled) {
      const appNumberRegex = /\b(?:([A-Z]{2})\s*)?(\d+[\d\s,/]+\d+)(?:\s*([A-Z]\d?))?\b/i;

      const applicationNumbers = [];

      for (const title of titles) {
        const match = appNumberRegex.exec(title);
        if (match) {
          const countryCode = match[1] || '';
          const cleanNumber = match[2].replace(/[\s,/]/g, '');
          const kindCode = match[3] || '';

          const appNumber = countryCode + cleanNumber + kindCode;

          if (cleanNumber.length >= 5 && cleanNumber.length <= 14) {
            applicationNumbers.push(appNumber);
          }
        }
      }

      if (applicationNumbers.length > 0) {
        const ipType = this.form.get('ipType')?.value ?? settings.ipTypeDefault;
        const ipTypeArray = this.checkAndSetIpType(ipType, false);
        return this.openByReferenceAndTypes(applicationNumbers, ipTypeArray, true);
      }
    }
    return of(false); // No application number was found or matching is disabled
  }

  /**
   * Handles Office.js DragAndDropEvent for drag and drop of emails into the taskpane.
   * @param event The Office drag and drop event data.
   */
  handleOfficeDragAndDrop(event: Office.DragoverEventData | Office.DropEventData) {
    switch (event.type) {
      case 'dragover': {
        this.isOfficeDragOver = true;
        this.isDragOver = true;

        // eslint-disable-next-line prefer-const
        let { newTarget, isOver } = this.findItemAtPoint(event.taskPaneX, event.taskPaneY);

        this.isDropPanelOver = isOver && newTarget === null;

        this.trifoldComponents.forEach((c) => {
          const shouldHighlight = newTarget !== null && c.item.identifier === newTarget.identifier && c.item.ipType === newTarget.ipType;

          this.updateItemState(c.item, { isDragOver: shouldHighlight });
        });

        break;
      }

      case 'drop': {
        const { newTarget, isOver } = this.findItemAtPoint(event.taskPaneX, event.taskPaneY);

        this.isOfficeDragOver = false;
        this.isDragOver = false;
        this.isDropPanelOver = false;

        this.trifoldComponents.filter((c) => c.item.state?.isDragOver).forEach((c) => this.updateItemState(c.item, { isDragOver: false }));

        if (!event.dataTransfer.files?.length) {
          this.loggerService.warn('[HomeComponent] No files in Office drop event');
          return;
        }

        const files = event.dataTransfer.files.map((f) => new File([f.fileContent], f.name));

        if (newTarget) {
          const trifoldComp = this.trifoldComponents.find(
            (c) => c.item.identifier === newTarget.identifier && c.item.ipType === newTarget.ipType
          );

          if (trifoldComp) {
            this.openUploadDialog(trifoldComp.item.identifier, trifoldComp.item.ipType, trifoldComp.uploadTrifold, files, true);
          }
        } else if (isOver) {
          this.snackBar
            .open('No case targeted - opening the Reference Library upload.', 'Dismiss', {
              duration: 2000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            })
            .afterDismissed()
            .subscribe(() => this.onOpenUploadGeneric(files));
        } else {
          this.snackBar.open('Please drop files onto an open case or the Upload Area.', 'Dismiss', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }

        break;
      }
    }
  }

  /**
   * Finds the item at the given task pane coordinates using elementFromPoint.
   * @param x The x-coordinate of the point to check.
   * @param y The y-coordinate of the point to check.
   * @returns An object containing a boolean indicating if the point is over an item and the item itself if found.
   */
  private findItemAtPoint(x: number, y: number): { isOver: boolean; newTarget: CommonIP | null } {
    const el = document.elementFromPoint(x, y);
    if (!el) return { isOver: false, newTarget: null };

    if (this.dropPanelContainer?.nativeElement.contains(el)) {
      return { isOver: true, newTarget: null };
    }

    for (const trifoldComp of this.trifoldComponents) {
      if (trifoldComp.elementRef.nativeElement.contains(el)) {
        return { isOver: true, newTarget: trifoldComp.item };
      }
    }

    return { isOver: false, newTarget: null };
  }

  /**
   * Determines the IP types to search based on the provided `ipType` string and optionally sets the form's IP type.
   *
   * @param ipType - The type of IP to search for. Can be 'All', 'Patent', 'Trademark', or any other string that defaults to 'Matter'.
   * @param setIpType - Optional boolean flag indicating whether to set the form's IP type. Defaults to `true`.
   * @returns An array of `IP.TrifTypeEnum` values corresponding to the determined IP types.
   */
  checkAndSetIpType(ipType: string, setIpType = true): IP.TrifTypeEnum[] {
    // determine which IP types we're going to search
    let singleIpTypeEnum;
    let ipTypeArray: IP.TrifTypeEnum[];
    if (ipType === 'All') {
      ipTypeArray = this.ipTypes.map((i) => i.key);
    } else {
      switch (ipType) {
        case 'Patent':
          singleIpTypeEnum = IP.TrifTypeEnum.Patent;
          break;
        case 'Trademark':
          singleIpTypeEnum = IP.TrifTypeEnum.Trademark;
          break;
        default:
          singleIpTypeEnum = IP.TrifTypeEnum.Matter;
      }
      ipTypeArray = [singleIpTypeEnum];
    }
    if (setIpType) {
      this.form.get('ipType')?.setValue(ipType, { emitEvent: false });
    }
    return ipTypeArray;
  }

  /**
   * Opens items by their reference identifiers and types.
   * @param identifiers - An array of reference identifiers to search for.
   * @param ipTypeArray - An array of IP types to search within.
   * @param serial - A boolean indicating whether to search by serial number (default is false).
   * @param filters - Optional filters to apply when opening the items.
   * @returns An observable that emits true if all identifiers were found, false otherwise.
   */
  openByReferenceAndTypes(
    identifiers: string[],
    ipTypeArray: IP.TrifTypeEnum[],
    serial = false,
    filters?: CommonIPStateTrifoldFiltersDialogData
  ) {
    const uniqueIdentifiers = Array.from(new Set(identifiers));
    let firstNonMatch = '';

    return from(uniqueIdentifiers).pipe(
      map((identifier) => {
        let errorCount = 0;
        return from(ipTypeArray).pipe(
          mergeMap((ipType) =>
            // search for the items we already have open in the taskpane
            this.store
              .select(serial ? selectItemBySerialAndType(identifier, ipType) : selectItemByIdentifierAndType(identifier, ipType))
              .pipe(
                take(1),
                map((item) => {
                  return { exists: !!item, ipType, item };
                })
              )
          ),
          mergeMap(({ exists, ipType, item }) => {
            if (exists && item) {
              this.updateItemState(item, { trifoldFilters: filters });
              return of(item);
            }

            // check if we have a cached response
            const cacheKey = `${serial ? 'serial' : 'cn'}-${identifier}-${ipType}`;
            const cacheErr = this.subjectMissCache.get(cacheKey);
            const obs = cacheErr
              ? throwError(() => cacheErr)
              : serial
                ? this.apiService.findIPBySerial(identifier, ipType, false)
                : this.apiService.findCommonIP(identifier, ipType, false);
            // first do a lightweight call because many of these will return "Not Found" responses
            return obs.pipe(
              // if found, then do a full call to get the details
              tap((item: CommonIP) => {
                this.fetchAndAddItem(item, filters);
              }),
              catchError((err) => {
                // cache the error
                this.subjectMissCache.set(cacheKey, err);
                if (++errorCount === ipTypeArray.length && firstNonMatch === '') {
                  // if we've not matched all iptypes for this identifier then record it
                  firstNonMatch = identifier;
                }
                return of(null);
              })
            );
          }, 2),
          filter((resp): resp is CommonIP => resp !== null),
          toArray()
        );
      }),
      mergeAll(),
      finalize(() => {
        // after we've processed all identifiers, set the 1st non match into search input
        if (firstNonMatch !== '') {
          this.form.get('search')?.setValue(firstNonMatch, { emitEvent: false });
        }
      }),
      map(() => uniqueIdentifiers.length > 0 && firstNonMatch === '')
    );
  }

  /**
   * Sends the uploaded files to a dialog.
   *
   * @param files - The list of files to be uploaded.
   * @param defaultTrifold - The default trifold to be used.
   * @param dialog - The dialog to send the files to.
   */
  sendUploadDialogFiles(files: File[], defaultTrifold: IP.TrifoldEnum, dialog: Office.Dialog | Window) {
    this.loaderService.show();

    from(files)
      .pipe(
        // Process each file based on whether it's a mail file or not
        mergeMap((file) => {
          if (/\.(msg|eml)$/i.test(file.name)) {
            // Process mail files and catch errors
            return FileBasedMailItem.fromFile(file, this.loggerService).pipe(
              mergeMap((mailItem) => this.sendUploadDialogOutlookItem(mailItem, defaultTrifold, dialog)),
              // If mail processing fails, return the original file to be treated as non-mail
              catchError(() => of(file))
            );
          } else {
            // Non-mail files are passed through directly
            return of(file);
          }
        }, 3),

        // Filter out nulls and collect only the non-mail files plus failed mail files
        filter((result): result is File => result instanceof File),
        toArray(),

        // Send all non-mail and failed mail files to the dialog
        tap((otherFiles) => {
          if (otherFiles.length > 0) {
            this.loggerService.debug(`[HomeComponent] Sending ${otherFiles.length} rest of files to dialog`);
            const fileRecs = otherFiles.map((file) => ({
              file,
              rec: FileWithProgressHelper.fromFile(file, defaultTrifold),
            }));

            const data: RelatedFileGroup = {
              id: 'manual',
              display: 'Local files',
              files: fileRecs.map((r) => r.rec),
            };

            this.messageUploadDialog({ type: 'fileGroup', data }, dialog);

            // Process each file and send its content to the dialog
            from(fileRecs)
              .pipe(
                mergeMap((rec) =>
                  convertFileToBase64(rec.file).pipe(
                    map((base64) => ({ type: 'fileData', data: { id: rec.rec.id, data: base64 } })),
                    catchError(() => of({ type: 'fileDataError', data: { id: rec.rec.id } }))
                  )
                ),
                tap((resp) => this.messageUploadDialog(resp, dialog)),
                map(() => null),
                toArray()
              )
              .subscribe();
          }
        }),
        finalize(() => this.loaderService.hide())
      )
      .subscribe();
  }

  /**
   * Sends the upload dialog Outlook item.
   *
   * @param item - The Outlook item to upload.
   * @param defaultTrifold - The default trifold to be used.
   * @param dialog - The upload dialog.
   * @returns An observable emitting when outlook item has been sent to dialog.
   */
  sendUploadDialogOutlookItem(item: MailItem, defaultTrifold: IP.TrifoldEnum, dialog: Office.Dialog | Window) {
    // convert the email
    const emailData = FileWithProgressHelper.fromMailItem(item, defaultTrifold);
    // convert all the attachments
    const files = [emailData];
    const contIds = item.attachments
      .filter((att) => {
        const attData = FileWithProgressHelper.fromMailAttachment(item, defaultTrifold, att);
        if (attData) {
          files.push(attData);
          return true;
        }
        return false;
      })
      .map((att) => att.id);
    contIds.push(emailData.id);

    // relay those to the dialog
    const data: RelatedFileGroup = { id: emailData.id, display: emailData.title, files };
    this.messageUploadDialog({ type: 'fileGroup', data }, dialog);

    // load the content of the email and attachments
    return item
      .fetchAllContent((attResp) => {
        const id = attResp.detail.type === 'att' ? `att-${attResp.detail.id}` : attResp.detail.id;
        if (attResp.success) {
          const file = attResp.value!;
          const data = file.format === 'base64' ? file.content : Buffer.from(file.content).toString('base64');
          this.messageUploadDialog({ type: 'fileData', data: { id, data } }, dialog);
        } else {
          this.loggerService.error('[HomeComponent] Error downloading attachment', attResp.error);
          this.messageUploadDialog({ type: 'fileDataError', data: { id } }, dialog);
        }
      }, this.$ngUploadDialogDestroyed)
      .pipe(
        catchError((error) => {
          this.loggerService.error('[HomeComponent] Error downloading attachments', error);
          contIds.forEach((id) => {
            this.messageUploadDialog({ type: 'fileDataError', data: { id } }, dialog);
          });
          return of([]);
        }),
        map(() => {})
      );
  }

  /**
   * Sends a message to the child dialog or the parent window.
   * @param message - The message to send. It can be either a string or an object that will be serialized to JSON.
   * @param dialog - The reference to the child dialog or the parent window.
   */
  messageUploadDialog(message: DialogMessageEvent | string, dialog: Office.Dialog | Window) {
    const messageToSend = typeof message === 'string' ? message : JSON.stringify(message);
    if (this.addinService.isOfficeActive) {
      (dialog as Office.Dialog).messageChild(messageToSend);
    } else if (dialog !== window) {
      (dialog as Window).postMessage(messageToSend, location.origin);
    }
  }

  /**
   * Opens items in the FTF application.
   * @param event - The event containing the item information.
   */
  onOpenInFTF(event: TrifoldItemEvent) {
    // build url base on the iptype
    const type = event.item.ipType === IP.TypeEnum.Patent ? 'Case' : event.item.ipType;
    const url = `${this.settingsService.getParsedBaseUrl()}index.jsp?type=${type}&redirectCase=${encodeURIComponent(event.item.identifier)}`;
    window.open(url, '_blank');
  }

  /**
   * Initiates a PDF preview for the specified document.
   * @param event - The event containing the document information.
   */
  onPdfPreview(event: TrifoldItemFileEvent) {
    this.pdfPreviewService.previewByFileId(event.doc.fileId!);
  }

  /**
   * Handles the help click event by opening the Clarivate help page in a new window.
   */
  onHelpClick() {
    window.open(
      `${this.settingsService.getParsedBaseUrl()}clarivatehelp.html`,
      'clarivatehelp',
      'toolbar=no,width=950,height=725,status=yes,resizable=yes,titlebar=no'
    );
  }

  /**
   * Handles the cookies preferences click event by opening the cookie preferences dialog.
   */
  onCookiesPreferencesClick() {
    this.pendoService.openCookiePreferences();
  }

  /**
   * Refreshes the specified item by making an API call to retrieve updated data.
   *
   * @param event - The trifold event that triggered the refresh.
   */
  onRefreshItem(event: TrifoldItemEvent) {
    this.refreshItems([event.item]);
  }

  /**
   * Handles workflow events for the specified item.
   * @param event - The event containing the item and workflow information.
   */
  onWorkflowEvent(event: TrifoldItemWorkflowEvent) {
    // update workflow in the item
    this.updateItemWorkflow(event.item, event.workflow.id!, event.workflow);
  }

  /**
   * Attaches the specified document to an Outlook email.
   * @param event - The event containing the document information.
   * @returns
   */
  onAttachToOutlook(event: TrifoldItemFileEvent) {
    if (!this.addinService.isOutlookCompose) return;

    this.apiService
      .downloadFile(event.doc.fileId!, event.doc.fileName!, event.item.ipType)
      .pipe(mergeMap((blob) => convertFileToBase64(blob)))
      .subscribe((base64File) => {
        this.addinService.outlookItems.forEach((item) => {
          item.addAttachment(base64File, event.doc.fileName!).subscribe((result) => {
            this.loggerService.debug(`[HomeComponent] Attachment added to Outlook item ${result}`);
          });
        });
      });
  }

  /**
   * Sends the selected item to the widget.
   * @param event - The event containing the item information.
   * @returns
   */
  onSendToWidget(event: TrifoldItemSelectionEvent) {
    if (!this.addinService.isWidgetActive) return;

    const selection = event.selection.reduce(
      (acc, selection) => {
        if (selection.type === 'reference') {
          acc.reference.push(convertCaseRefToReferenceDocument(selection.doc, event.item));
        } else {
          acc.trifold.push(convertWsFileToDocument(selection.doc, event.item));
        }
        return acc;
      },
      { trifold: [], reference: [] } as DocumentSelections
    );

    const action: DocumentAction = { case: convertCommonIPToIPCase(event.item), selection };
    this.addinService.recordEvent(FTFEvent.DocAction, action);
  }

  /**
   * Initiates the upload of a new version for the specified document.
   * @param event - The event containing the document information.
   * @returns
   */
  onUploadNewVersion(event: TrifoldItemFileEvent) {
    if (!this.isWord || event.type === 'reference') return;

    const { doc } = event;
    const { item } = event;

    getWordContext(this.loggerService).subscribe((context) => {
      const validation = [];
      // check that document is saved
      if (!context.document.saved) {
        validation.push('Please save the document before uploading a new version.');
      }
      // check that the document is not locked
      if ('status' in doc) {
        if (doc.status === 'Locked') {
          validation.push('Cannot upload new version.  Document is locked.');
        } else if (doc.status === 'Checked-Out') {
          validation.push('Cannot upload new version.  Document is checked out.');
        }
      }
      // check that the document is not export controlled
      if (this.isExportControlled(doc, item)) {
        validation.push('Cannot upload new version.  Document is export controlled.');
      }

      if (validation.length > 0) {
        validation.forEach((msg) => {
          this.snackBar.open(msg, 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            politeness: 'assertive',
            duration: 5000,
          });
        });
        return;
      }

      const ref = this.responseDialog.open(UploadVersionDialogComponent, {
        data: { item, doc, fileName: context.fileName },
        width: '320px',
        minWidth: '85vw',
      });

      ref.afterClosed().subscribe((resp: undefined | { value: UploadVersionDialogData }) => {
        if (resp) {
          this.fetchAndUploadDocVersion(resp.value.item, resp.value.doc);
        }
      });
    });
  }
  /**
   * Fetches the document as a compressed blob and uploads it as a new version.
   * @param item - The item to which the document belongs.
   * @param doc - The document to upload.
   */
  fetchAndUploadDocVersion(item: CommonIP, doc: WsFile) {
    const stats: UploadStats = {
      items: [{ item, successFiles: [], failureFiles: [] }],
      emails: {},
    };
    this.loaderService.show();
    getDocumentAsCompressedObservable()
      .pipe(
        mergeMap((blob) => this.apiService.uploadFileVersion(doc.fileId!, item.identifier, doc.fileName!, blob, item.ipType)),
        tap(() => {
          stats.items[0].successFiles.push(doc.fileName!);
        }),
        catchError((err) => {
          this.loggerService.error('[HomeComponent] Error uploading new version', err);
          stats.items[0].failureFiles.push(doc.fileName!);
          return err;
        }),
        finalize(() =>
          this.ngZone.run(() => {
            this.loaderService.hide();
            this.refreshItems([item], stats);
          })
        )
      )
      .subscribe();
  }

  /**
   * Checks if the document is subject to export control.
   * @param doc - The document to check.
   * @param item - The item associated with the document.
   * @returns True if the document is subject to export control, false otherwise.
   */
  isExportControlled(doc: WsFile | ReferenceDocument, item: CommonIP) {
    return (
      'technicalDocument' in doc &&
      doc.technicalDocument === 'Y' &&
      item.exportControlStatus === 'Y' &&
      this.accountService.userSubjectToExportControl
    );
  }

  /**
   * Downloads the specified document.
   * @param event - The event containing the document information.
   */
  onDownloadItem(event: TrifoldItemFileEvent) {
    // user is subject to export control so bail out
    if (this.isExportControlled(event.doc, event.item)) return;
    this.apiService.downloadFile(event.doc.fileId!, event.doc.fileName!, event.item.ipType).subscribe((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = event.doc.fileName!;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    });
  }

  /**
   * Initiates the checkout process for the specified document.
   * @param event - The event containing the document information.
   * @returns
   */
  onCheckout(event: TrifoldItemFileEvent) {
    if (this.isExportControlled(event.doc, event.item)) return;
    if (event.type !== 'trifold') return;
    const doc = event.doc as WsFile;
    if (doc.status !== 'OK') return;
    this.store.dispatch(ItemsActions.updateDocStatus({ item: event.item, id: doc.fileId!, action: 'checkout' }));
  }
  /**
   * Initiates the undo checkout process for the specified document.
   * @param event - The event containing the document information.
   * @returns
   */
  onUndoCheckout(event: TrifoldItemFileEvent) {
    if (this.isExportControlled(event.doc, event.item)) return;
    if (event.type !== 'trifold') return;
    const doc = event.doc as WsFile;
    if (doc.status !== 'Checked-Out') return;
    this.store.dispatch(ItemsActions.updateDocStatus({ item: event.item, id: doc.fileId!, action: 'undocheckout' }));
  }

  /**
   * Initiates the unlock process for the specified document.
   * @param event - The event containing the document information.
   * @returns
   */
  onUnlockItem(event: TrifoldItemFileEvent) {
    if (this.isExportControlled(event.doc, event.item)) return;
    if (event.type !== 'trifold') return;
    const doc = event.doc as WsFile;
    if (doc.status !== 'Locked') return;
    this.store.dispatch(ItemsActions.updateDocStatus({ item: event.item, id: doc.fileId!, action: 'unlock' }));
  }

  /**
   * Initiates the lock process for the specified document.
   * @param event - The event containing the document information.
   * @returns
   */
  onLockItem(event: TrifoldItemFileEvent) {
    if (this.isExportControlled(event.doc, event.item)) return;
    if (event.type !== 'trifold') return;
    const doc = event.doc as WsFile;
    if (doc.status !== 'OK') return;
    this.store.dispatch(ItemsActions.updateDocStatus({ item: event.item, id: doc.fileId!, action: 'lock' }));
  }

  /**
   * Initiates the delete process for the specified document.
   * @param event - The event containing the document information.
   * @returns
   */
  onDeleteItem(event: TrifoldItemFileEvent) {
    const { doc } = event;
    if ('status' in doc) {
      if (doc.status === 'Locked') {
        this.snackBar.open('Locked files must first be unlocked before deleting', 'Dismiss', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          politeness: 'assertive',
          duration: 5000,
        });
        return;
      }
      this.store.dispatch(ItemsActions.deleteItemDoc({ item: event.item, id: event.doc.fileId! }));
    }
  }

  /**
   * Initiates the file browsing process.
   * @param event - The event containing the file input information.
   * @returns
   */
  onBrowseFiles(event: Event) {
    // Office doesn't let us use showOpenFilePicker({ multiple: true }) within their frame
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    const files = Array.from(target.files);
    // have to delay it otherwise file api will keep popup from opening
    setTimeout(() => this.onOpenUploadGeneric(files), 100);
  }

  /**
   * Fetches and adds an item to the store.
   * @param item
   * @param additionalTrifoldFilters
   */
  fetchAndAddItem(item: CommonIPLite, additionalTrifoldFilters?: CommonIPStateTrifoldFiltersDialogData) {
    this.store.dispatch(
      ItemsActions.addItemLite({
        item,
        skipReference: !this.accountService.hasRole('ROLE_REFERENCE_ACCESS'),
        skipWorkflow: !this.accountService.hasRole('ROLE_WORKFLOW_ACCESS'),
        trifoldFilters: additionalTrifoldFilters!,
      })
    );
  }

  /**
   * Updates an item in the store.
   * @param item - The item to update.
   * @param skipState - Whether to skip the state update.
   */
  updateItem(item: CommonIPLite & Partial<CommonIP>, skipState = true) {
    this.store.dispatch(ItemsActions.updateItem({ item, skipState }));
  }

  /**
   * Updates the state of an item in the store.
   * @param item - The item to update.
   * @param itemState - The new state to apply to the item.
   * @returns
   */
  updateItemState(item: CommonIP, itemState: Partial<CommonIPState>) {
    // Bail out if all the keys of itemState match those in item.state (no change)
    const keys = Object.keys(itemState) as (keyof CommonIPState)[];
    if (
      keys.every((key) => {
        if (!item.state) return false; // if no state, then we can't match
        if (key === 'trifoldFilters') {
          // special case for trifoldFilters, we need to check if the filters are the same
          return areFiltersEqual(item.state.trifoldFilters || {}, itemState.trifoldFilters || {});
        } else {
          return item.state[key] === itemState[key];
        }
      })
    ) {
      return;
    }
    // request the update
    this.store.dispatch(ItemsActions.updateItemState({ item, itemState }));
  }

  /**
   * Updates the trifold document for an item.
   * @param item - The item to update.
   * @param trifold - The new trifold value.
   * @param id - The ID of the document.
   * @param doc - The document data to update.
   */
  updateItemTrifoldDocument(item: CommonIPLite, trifold: IP.TrifoldEnum, id: number, doc: Partial<WsFile>) {
    // request the update
    this.store.dispatch(ItemsActions.updateItemTrifoldDocument({ item, trifold, id, doc }));
  }

  /**
   * Updates the workflow for an item.
   * @param item - The item to update.
   * @param id - The ID of the document.
   * @param workflow - The new workflow data to apply.
   */
  updateItemWorkflow(item: CommonIPLite, id: number, workflow: Partial<WorkflowDO>) {
    this.store.dispatch(ItemsActions.updateItemWorkflow({ item, id, workflow }));
  }

  /**
   * Removes an item from the store.
   * @param item - The item to remove.
   */
  removeItem(item: CommonIPLite) {
    this.store.dispatch(ItemsActions.removeItem({ item }));
  }

  /**
   * Refreshes an item based on its identifier and IP type.
   *
   * @param refreshItems - The items to be refreshed
   * @param stats - The upload stats to display in the dialog.
   */
  refreshItems(refreshItems: CommonIPLite[], stats: UploadStats | null = null) {
    this.store.dispatch(
      ItemsActions.refreshItems({
        refreshItems,
        skipReference: !this.accountService.hasRole('ROLE_REFERENCE_ACCESS'),
        skipWorkflow: !this.accountService.hasRole('ROLE_WORKFLOW_ACCESS'),
      })
    );

    if (!stats) return;

    const success = stats.items.reduce((acc, s) => acc + s.successFiles.length, 0);
    const failure = stats.items.reduce((acc, s) => acc + s.failureFiles.length, 0);
    if (success > 0 || failure > 0) {
      this.responseDialog.open(UploadResultsDialogComponent, {
        width: '300px',
        data: { stats: stats.items },
      });
    }
  }

  /**
   * Updates the filters for an item.
   * @param event - The filter update event.
   */
  onUpdateFilters(event: TrifoldItemFilterEvent) {
    this.updateItemState(event.item, { trifoldFilters: event.filters });
  }

  /**
   * Formats the display value for a search result.
   * @param value - The value to format.
   * @returns The formatted display value.
   */
  searchDisplayFn(value: CommonIP | string) {
    return typeof value === 'string' ? value : (value?.identifier ?? '');
  }

  /**
   * Tracks the item by its unique identifier.
   * @param index - The index of the item in the list.
   * @param item - The item to track.
   * @returns The unique identifier for the item.
   */
  trackByItem(index: number, item: CommonIP): string {
    return `${item.ipType}|${item.identifier}`;
  }

  /**
   * Determines the default trifold for the current item.
   * @returns The default trifold value.
   */
  determineDefaultTrifold(): IP.TrifoldEnum {
    const ipType = this.form.get('ipType')?.getRawValue();
    return ipType && ipType !== 'All' ? (this.settingsService.getDefaultTrifoldsMap()[ipType as IP.TrifTypeEnum] ?? 'L') : 'L';
  }

  /**
   *  Dismisses the currently displayed snackbar notification.
   */
  dismissSnackbar() {
    this.snackBar.dismiss();
  }

  /**
   *
   * @readonly
   * @type {*}
   */
  get isMultiSelectSupported() {
    return this.addinService.supportsMultiSelect;
  }

  /**
   * Indicates whether the current environment is Word.
   *
   * @readonly
   * @type {*}
   */
  get isWord() {
    return this.addinService.isWord;
  }

  /**
   * Indicates whether the current environment is Outlook.
   *
   * @readonly
   * @type {*}
   */
  get isOutlook() {
    return this.addinService.isOutlook;
  }

  /**
   * Indicates whether the current environment is Outlook Read.
   *
   * @readonly
   * @type {*}
   */
  get isOutlookRead() {
    return this.addinService.isOutlookRead;
  }

  /**
   * Indicates whether the current environment is Outlook Compose.
   *
   * @readonly
   * @type {*}
   */
  get isOutlookCompose() {
    return this.addinService.isOutlookCompose;
  }

  /**
   * Indicates whether the current environment is Excel.
   *
   * @readonly
   * @type {*}
   */
  get isExcel() {
    return this.addinService.isExcel;
  }

  /**
   * Indicates whether the current environment is a widget.
   *
   * @readonly
   * @type {*}
   */
  get widgetProductName() {
    return this.addinService.isWidgetActive ? (this.addinService.xprops?.productName ?? null) : null;
  }

  /**
   * Indicates the current layout orientation.
   *
   * @readonly
   * @type {("Vertical" | "Horizontal")}
   */
  get orientation() {
    return this.addinService.isVerticalLayout ? 'Vertical' : 'Horizontal';
  }

  get accessibleIpTypes(): IP.TrifTypeEnum[] {
    return this.accountService.trifIpTypes.map((t) => t.key);
  }

  get hasReferenceAccess(): boolean {
    return this.accountService.hasRole('ROLE_REFERENCE_ACCESS');
  }
}
